const DATABASE_NAME = "light-boat-client";
const DATABASE_VERSION = 1;
const nativeRuntime = "__TAURI_INTERNALS__" in window;

let browserDatabasePromise;
let nativeDatabasePromise;

export function isNativeDatabase() {
  return nativeRuntime;
}

function openBrowserDatabase() {
  if (browserDatabasePromise) return browserDatabasePromise;

  browserDatabasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("notes")) {
        database.createObjectStore("notes", { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains("outbox")) {
        database.createObjectStore("outbox", { keyPath: "noteId" });
      }
      if (!database.objectStoreNames.contains("meta")) {
        database.createObjectStore("meta", { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return browserDatabasePromise;
}

function openNativeDatabase() {
  if (!nativeDatabasePromise) {
    nativeDatabasePromise = import("@tauri-apps/plugin-sql").then(({ default: Database }) =>
      Database.load("sqlite:light-boat-client.db"),
    );
  }
  return nativeDatabasePromise;
}

async function useBrowserStore(storeName, mode, callback) {
  const database = await openBrowserDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result;

    try {
      result = callback(store);
    } catch (error) {
      reject(error);
      return;
    }

    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error("Database transaction aborted"));
  });
}

function browserRequestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function nativeRowToNote(row) {
  return {
    id: row.id,
    body: row.body,
    createdAt: Number(row.createdAt),
    updatedAt: Number(row.updatedAt),
    deletedAt: row.deletedAt == null ? null : Number(row.deletedAt),
    pinned: Boolean(row.pinned),
    serverRevision: Number(row.serverRevision || 0),
    syncedBody: row.syncedBody == null ? null : row.syncedBody,
  };
}

function browserRowToNote(row) {
  if (!row) return row;
  return {
    ...row,
    serverRevision: Number(row.serverRevision || 0),
    syncedBody: row.syncedBody ?? null,
  };
}

export async function getAllNotes() {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    const rows = await database.select(
      `SELECT id, body, created_at AS "createdAt", updated_at AS "updatedAt",
              deleted_at AS "deletedAt", pinned, server_revision AS "serverRevision",
              synced_body AS "syncedBody"
       FROM notes`,
    );
    return rows.map(nativeRowToNote);
  }

  const database = await openBrowserDatabase();
  const transaction = database.transaction("notes", "readonly");
  return (await browserRequestResult(transaction.objectStore("notes").getAll())).map(browserRowToNote);
}

export async function getNote(id) {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    const rows = await database.select(
      `SELECT id, body, created_at AS "createdAt", updated_at AS "updatedAt",
              deleted_at AS "deletedAt", pinned, server_revision AS "serverRevision",
              synced_body AS "syncedBody"
       FROM notes WHERE id = $1`,
      [id],
    );
    return rows[0] ? nativeRowToNote(rows[0]) : undefined;
  }

  const database = await openBrowserDatabase();
  const transaction = database.transaction("notes", "readonly");
  return browserRowToNote(await browserRequestResult(transaction.objectStore("notes").get(id)));
}

export async function putNote(note) {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    await database.execute(
      `INSERT INTO notes (
          id, body, created_at, updated_at, deleted_at, pinned, server_revision, synced_body
       ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8)
       ON CONFLICT(id) DO UPDATE SET
          body = excluded.body,
          created_at = excluded.created_at,
          updated_at = excluded.updated_at,
          deleted_at = excluded.deleted_at,
          pinned = excluded.pinned,
          server_revision = excluded.server_revision,
          synced_body = excluded.synced_body`,
      [
        note.id,
        note.body,
        note.createdAt,
        note.updatedAt,
        note.deletedAt,
        note.pinned ? 1 : 0,
        note.serverRevision || 0,
        note.syncedBody ?? null,
      ],
    );
    return;
  }
  return useBrowserStore("notes", "readwrite", (store) =>
    store.put({ ...note, syncedBody: note.syncedBody ?? null }),
  );
}

export async function getOutboxOperations() {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    const rows = await database.select("SELECT operation_json AS operationJson FROM outbox");
    return rows.map((row) => JSON.parse(row.operationJson));
  }

  const database = await openBrowserDatabase();
  const transaction = database.transaction("outbox", "readonly");
  return browserRequestResult(transaction.objectStore("outbox").getAll());
}

export async function getOutboxOperation(noteId) {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    const rows = await database.select(
      "SELECT operation_json AS operationJson FROM outbox WHERE note_id = $1",
      [noteId],
    );
    return rows[0] ? JSON.parse(rows[0].operationJson) : undefined;
  }

  const database = await openBrowserDatabase();
  const transaction = database.transaction("outbox", "readonly");
  return browserRequestResult(transaction.objectStore("outbox").get(noteId));
}

export async function putOutboxOperation(operation) {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    await database.execute(
      `INSERT INTO outbox (note_id, operation_json) VALUES ($1, $2)
       ON CONFLICT(note_id) DO UPDATE SET operation_json = excluded.operation_json`,
      [operation.noteId, JSON.stringify(operation)],
    );
    return;
  }
  return useBrowserStore("outbox", "readwrite", (store) => store.put(operation));
}

export async function deleteOutboxOperation(noteId, expectedOpId) {
  const current = await getOutboxOperation(noteId);
  if (!current || current.opId !== expectedOpId) return false;

  if (nativeRuntime) {
    const database = await openNativeDatabase();
    await database.execute("DELETE FROM outbox WHERE note_id = $1", [noteId]);
    return true;
  }
  await useBrowserStore("outbox", "readwrite", (store) => store.delete(noteId));
  return true;
}

export async function getMeta(key, fallback = null) {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    const rows = await database.select("SELECT value_json AS valueJson FROM meta WHERE key = $1", [key]);
    return rows[0] ? JSON.parse(rows[0].valueJson) : fallback;
  }

  const database = await openBrowserDatabase();
  const transaction = database.transaction("meta", "readonly");
  const result = await browserRequestResult(transaction.objectStore("meta").get(key));
  return result ? result.value : fallback;
}

export async function setMeta(key, value) {
  if (nativeRuntime) {
    const database = await openNativeDatabase();
    await database.execute(
      `INSERT INTO meta (key, value_json) VALUES ($1, $2)
       ON CONFLICT(key) DO UPDATE SET value_json = excluded.value_json`,
      [key, JSON.stringify(value)],
    );
    return;
  }
  return useBrowserStore("meta", "readwrite", (store) => store.put({ key, value }));
}
